import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Bar, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { get, downloadFile, toastError } from "@/lib/api";
import { fmtDate, num } from "@/lib/format";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ErrorState } from "@/components/common/States";
import { FileDown, Loader2, UtensilsCrossed, Info } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Fact, AccommodationBrief, AlertList, dayLabel, dayLabelLong } from "./AccommodationBits";

const C = { breakfast: "#c3c9d4", lunch: "#8a93a3", dinner: "#14171f", gap: "#2670F2", limit: "#F43F5E" };

const Tip = ({ active, payload, limit }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0]?.payload || {};
  const rows = [["Frokost", r.breakfast], ["Lunsj", r.lunch], ["Middag (opphold)", r.dinner], r.gap ? ["+ rotasjon uten opphold", r.gap] : null, ["Matpakker", r.packed], ["Ankomster / avreiser", `${r.arrivals} / ${r.departures}`], limit ? ["Belastning", `${r.load_pct} %`] : null].filter(Boolean);
  return (
    <div className="rounded-lg bg-card px-3 py-2 text-[12px] shadow-pop" data-testid="meals-chart-tooltip">
      <div className="mb-1 font-medium">{dayLabelLong(r.date)}{r.level === "danger" ? <span className="ml-1.5 text-rose-600">over kapasitet</span> : r.level === "warn" ? <span className="ml-1.5 text-amber-700">nær kapasitet</span> : null}</div>
      {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 text-muted-foreground tabular"><span>{k}</span><span className="font-mono text-foreground">{v ?? 0}</span></div>)}
    </div>
  );
};

const MealsChart = ({ rows, limit, height = 220, minWidth = 0 }) => (
  <div className={cn(minWidth > 0 && "overflow-x-auto scrollbar-thin")} data-testid="meals-chart">
    <div style={{ height, minWidth: minWidth || undefined }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={rows.length > 21 ? 2 : 6} barGap={1}>
          <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d, rows.length > 21 ? "d." : "EEEEEE d.")} tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} interval={rows.length > 28 ? 3 : rows.length > 14 ? 1 : 0} />
          <YAxis width={30} tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip content={<Tip limit={limit} />} cursor={{ fill: "rgba(16,24,40,0.04)" }} />
          {limit && <ReferenceLine y={limit} stroke={C.limit} strokeDasharray="4 3" strokeOpacity={0.8} label={{ value: `Kantine ${limit}`, position: "insideTopRight", fontSize: 10, fill: C.limit }} />}
          <Bar dataKey="breakfast" name="Frokost" fill={C.breakfast} maxBarSize={14} isAnimationActive={false} />
          <Bar dataKey="lunch" name="Lunsj" fill={C.lunch} maxBarSize={14} isAnimationActive={false} />
          <Bar dataKey="dinner" name="Middag" stackId="d" fill={C.dinner} fillOpacity={0.85} maxBarSize={14} isAnimationActive={false} />
          <Bar dataKey="gap" name="Rotasjon" stackId="d" fill={C.gap} fillOpacity={0.4} radius={[2, 2, 0, 0]} maxBarSize={14} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Th = ({ children, className, right }) => <th className={cn("py-2 pr-3 font-medium", right ? "text-right" : "text-left", className)}>{children}</th>;
const Td = ({ children, className, right, mono }) => <td className={cn("py-2 pr-3", right && "text-right", mono && "font-mono tabular", className)}>{children}</td>;
const LEVEL_TEXT = { danger: "text-rose-600 dark:text-rose-400 font-semibold", warn: "text-amber-700 dark:text-amber-300 font-medium" };

const DayTable = ({ rows, today, limit }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[13px]" data-testid="meals-day-table">
      <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
        <tr className="border-b"><Th>Dato</Th><Th right>Frokost</Th><Th right>Lunsj</Th><Th right>Middag</Th><Th right>Matpakker</Th><Th right>Inn / ut</Th>{limit && <Th right>Belastning</Th>}<Th>Crew på camp</Th></tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isToday = r.date === today;
          return (
            <tr key={r.date} className={cn("border-b border-border/70", r.weekend && "bg-muted/40", isToday && "bg-primary/[0.04]")} data-testid={`meals-row-${r.date}`}>
              <Td className={cn("whitespace-nowrap", isToday && "font-medium text-foreground")}>{r.weekday} {dayLabel(r.date)}{isToday && <span className="ml-1.5 text-[11px] text-primary">i dag</span>}</Td>
              <Td right mono>{r.breakfast}</Td>
              <Td right mono>{r.lunch}</Td>
              <Td right mono className={cn(LEVEL_TEXT[r.level])}>{r.dinner_forecast}{r.gap > 0 && <span className="ml-1 text-[11px] font-normal text-primary" title={`${r.dinner} planlagte opphold + ${r.gap} forventet fra rotasjon`}>(+{r.gap})</span>}</Td>
              <Td right mono>{r.packed || <span className="text-muted-foreground">–</span>}</Td>
              <Td right mono className="text-muted-foreground">{r.arrivals || r.departures ? `${r.arrivals} / ${r.departures}` : "–"}</Td>
              {limit && <Td right mono className={cn(LEVEL_TEXT[r.level])}>{r.load_pct} %</Td>}
              <Td className="max-w-[260px] truncate text-[12px] text-muted-foreground">{r.crews_on?.length ? r.crews_on.join(", ") : "–"}</Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const DayCards = ({ rows, today, limit }) => (
  <ul className="divide-y rounded-lg border" data-testid="meals-day-cards">
    {rows.map((r) => {
      const isToday = r.date === today;
      return (
        <li key={r.date} className={cn("px-3 py-2.5", r.weekend && "bg-muted/40", isToday && "bg-primary/[0.04]")} data-testid={`meals-card-${r.date}`}>
          <div className="flex items-baseline justify-between text-[12.5px]">
            <span className={cn(isToday && "font-medium")}>{r.weekday} {dayLabel(r.date)}{isToday && <span className="ml-1.5 text-[11px] text-primary">i dag</span>}</span>
            {limit && <span className={cn("font-mono text-[11.5px] tabular text-muted-foreground", LEVEL_TEXT[r.level])}>{r.load_pct} %</span>}
          </div>
          <div className="mt-1 grid grid-cols-4 gap-2 font-mono text-[13px] tabular">
            {[["Frokost", r.breakfast], ["Lunsj", r.lunch], ["Middag", r.dinner_forecast], ["Matpakke", r.packed]].map(([l, v]) => (
              <div key={l}><div className={cn("font-semibold", l === "Middag" && LEVEL_TEXT[r.level])}>{v}</div><div className="font-sans text-[10.5px] uppercase tracking-wide text-muted-foreground">{l}</div></div>
            ))}
          </div>
        </li>
      );
    })}
  </ul>
);

/**
 * Måltid / kantine – daily kitchen counts (frokost · lunsj · middag · matpakke) from stays + crew rotation,
 * against the canteen capacity allocated to the camp. Deterministic; the CSV is the kjøkkenliste sent to the kitchen.
 */
export const MealsView = ({ projectId, projectNumber, hasPersons, className }) => {
  const wide = useMediaQuery("(min-width: 1024px)");
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["ink", "meals", projectId, days], queryFn: () => get(`/innkvartering/projects/${projectId}/meals`, { days }), placeholderData: keepPreviousData, refetchInterval: 120_000 });
  const d = q.data;

  const exportCsv = async () => {
    setBusy(true);
    try {
      const name = await downloadFile(`/innkvartering/projects/${projectId}/meals`, { days, format: "csv" }, `Kjokkenliste-${projectNumber || "camp"}.csv`);
      toast.success("Kjøkkenliste eksportert", { description: name });
    } catch (e) { toastError(e, "Kunne ikke eksportere kjøkkenlisten"); } finally { setBusy(false); }
  };

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3" data-testid="meals-toolbar">
      <ToggleGroup type="single" value={String(days)} onValueChange={(v) => v && setDays(Number(v))} className="h-8 rounded-md border p-0.5" data-testid="meals-days-toggle">
        {[7, 14, 28].map((n) => <ToggleGroupItem key={n} value={String(n)} className="h-7 px-2.5 text-[12px]" data-testid={`meals-days-${n}`}>{n} dager</ToggleGroupItem>)}
      </ToggleGroup>
      {d && <span className="text-[12.5px] text-muted-foreground tabular" data-testid="meals-range-label">{dayLabel(d.today)} – {dayLabel(d.rows[d.rows.length - 1]?.date)} · {num(d.totals.meals)} måltider</span>}
      <div className="ml-auto flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-8" onClick={exportCsv} disabled={busy || !d} data-testid="meals-export-csv-button">{busy ? <Loader2 className="animate-spin" /> : <FileDown />} Kjøkkenliste (CSV)</Button>
      </div>
    </div>
  );

  if (q.isError) return <div className={cn("space-y-4", className)}>{toolbar}<ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  if (!d) return <div className={cn("space-y-4", className)} data-testid="meals-loading">{toolbar}<div className="grid grid-cols-3 gap-4 sm:grid-cols-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div><Skeleton className="h-56" /></div>;

  const t = d.today_counts;
  const cap = d.canteen;
  const limit = cap?.persons_capacity || null;
  const peakTone = d.peak?.level === "danger" ? "danger" : d.peak?.level === "warn" ? "warning" : undefined;
  const nobody = !hasPersons && d.totals.meals === 0;
  const alerts = d.alerts.map((a, i) => ({ ...a, kind: `${a.severity}-${a.date}-${i}` }));

  return (
    <div className={cn("space-y-5", className)} data-testid="meals-view">
      {toolbar}

      {nobody ? (
        <div className="rounded-lg border border-dashed px-6 py-12 text-center" data-testid="meals-empty">
          <UtensilsCrossed className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 text-[14px] font-medium">Kunden drifter campen</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">Opphold føres ikke i Stord OS her, så kjøkkentall kan ikke beregnes. {cap?.persons_capacity ? `Kantinekapasiteten er ${cap.persons_capacity} personer per måltid.` : ""}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-x-5 gap-y-4 sm:grid-cols-6" data-testid="meals-facts">
            <Fact label="Frokost i dag" value={num(t.breakfast)} sub="personer" testId="meals-fact-breakfast" />
            <Fact label="Lunsj i dag" value={num(t.lunch)} sub="personer" testId="meals-fact-lunch" />
            <Fact label="Middag i dag" value={num(t.dinner_forecast)} sub={t.gap ? `${t.dinner} opphold + ${t.gap} rotasjon` : `${t.arrivals} ankommer`} tone={t.level === "danger" ? "danger" : t.level === "warn" ? "warning" : undefined} testId="meals-fact-dinner" />
            <Fact label="Matpakker" value={num(t.packed)} sub={t.packed ? "reiser i dag" : "ingen avreiser"} testId="meals-fact-packed" />
            <Fact label="Kantine" value={limit ? num(limit) : "–"} sub={limit ? "per måltid" : "ekstern kantine"} testId="meals-fact-capacity" />
            <Fact label="Topp" value={d.peak ? num(d.peak.peak) : "–"} sub={d.peak ? dayLabel(d.peak.date) : ""} mono tone={peakTone} testId="meals-fact-peak" />
          </div>

          <div className="grid gap-5 lg:grid-cols-12">
            <AccommodationBrief sentences={d.brief} compact testId="meals-brief" className="lg:col-span-8" meta="Beregnet deterministisk fra opphold, crew-rotasjon og kantinemoduler allokert til campen" />
            {alerts.length > 0 && <AlertList alerts={alerts} testId="meals-alerts" className="lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-5" />}
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="meals-legend">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#c3c9d4]" aria-hidden /> Frokost</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#8a93a3]" aria-hidden /> Lunsj</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-foreground/85" aria-hidden /> Middag</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-primary/40" aria-hidden /> Forventet fra rotasjon</span>
              {limit && <span className="inline-flex items-center gap-1.5"><span className="h-[1px] w-3 border-t border-dashed border-rose-500" aria-hidden /> Kantinekapasitet {limit}</span>}
            </div>
            <MealsChart rows={d.rows} limit={limit} height={wide ? 220 : 180} minWidth={wide ? 0 : Math.max(520, d.rows.length * 40)} />
          </div>

          <div className="grid gap-6 lg:grid-cols-12">
            <section className="min-w-0 lg:col-span-8">
              <h3 className="mb-2 text-[13.5px] font-semibold tracking-tight">Per dag</h3>
              {wide ? <DayTable rows={d.rows} today={d.today} limit={limit} /> : <DayCards rows={d.rows} today={d.today} limit={limit} />}
            </section>
            <aside className="space-y-5 lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-6">
              <section data-testid="meals-canteen">
                <h3 className="text-[13.5px] font-semibold tracking-tight">Kantine</h3>
                <p className="mt-1.5 text-[12.5px] leading-5 text-foreground/85">{cap.label}</p>
                {cap.modules?.length > 0 && (
                  <ul className="mt-2 divide-y rounded-lg border text-[12.5px]">
                    {cap.modules.map((m) => <li key={m.code} className="flex items-center justify-between px-3 py-1.5"><span><span className="font-mono font-semibold">{m.code}</span> <span className="text-muted-foreground">· {m.type}</span></span><span className="font-mono tabular text-muted-foreground">{m.seats} pl.</span></li>)}
                  </ul>
                )}
              </section>
              <section data-testid="meals-weeks">
                <h3 className="text-[13.5px] font-semibold tracking-tight">Per uke</h3>
                <ul className="mt-2 divide-y rounded-lg border text-[12.5px]">
                  {d.weeks.map((w) => (
                    <li key={w.key} className="flex items-center justify-between px-3 py-1.5" data-testid={`meals-week-${w.key}`}>
                      <span><span className="font-semibold">{w.label}</span> <span className="text-muted-foreground">fra {dayLabel(w.start)} · {w.days} d</span></span>
                      <span className="font-mono tabular"><span className="font-semibold">{num(w.meals)}</span> <span className="text-muted-foreground">måltider · topp {w.peak}</span></span>
                    </li>
                  ))}
                </ul>
              </section>
              {d.employers?.length > 0 && (
                <section data-testid="meals-employers">
                  <h3 className="text-[13.5px] font-semibold tracking-tight">Arbeidsgivere på camp</h3>
                  <p className="mt-1.5 text-[12.5px] leading-5 text-muted-foreground">{d.employers.join(" · ")}</p>
                </section>
              )}
            </aside>
          </div>

          <p className="flex items-start gap-2 text-[11.5px] leading-4 text-muted-foreground" data-testid="meals-footnote">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Frokost = overnattet natten før (inkl. de som reiser). Lunsj = på camp hele dagen. Middag = overnatter i natt (inkl. ankomster). Matpakke = reiser i dag. Der crew-rotasjonen tilsier flere enn det er planlagte opphold, vises differansen som «forventet fra rotasjon».</span>
          </p>
        </>
      )}
    </div>
  );
};
